import { useEffect, useMemo, useReducer, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import {
  C2S,
  S2C,
  type AuthNoticePayload,
  type AuthOtpType,
  type OAuthProvider,
  type AuthStatePayload,
  type RoomConfig,
  type RoomMode,
  type LobbyUpdatePayload,
  type GameView,
  type RoomJoinedPayload,
  type ReactionId,
  type PlayerReactionPayload,
  type MoveAcceptedPayload,
  type GameOverPayload,
  type GameStartPayload,
  type GameType,
  type UserTier,
  type BillingPage,
  type PaymentRecord,
  type CouponQuote,
  type RoomSessionHistory,
  type PauseActionType,
  type VoiceParticipantsPayload,
  type VoiceSignalPayload,
  type VoiceSignalRelayPayload,
  type PublicRoomsPayload,
  type MatchmakingStatusPayload,
  type LeaderboardPayload,
  type FriendsDataPayload,
  type PublicPlayerProfile,
  type PaymentInfoPayload,
  type TableThrowBroadcast,
  type VoiceTauntBroadcast,
  type ThrowableId,
  type VoiceTauntId,
  type SubmitFeedbackPayload,
  type PlayerKickedPayload,
  MEMBERSHIP_PRICE_MNT,
} from '@big2/shared';
import {
  playShuffleSound,
  playCardPlaceSound,
  playPassSound,
  playGameOverSound,
  playMatchFoundSound,
} from '../sound.js';
import { setAnalyticsUser, trackEvent } from '../analytics.js';

import { type ClientState, type GameActions } from './socket/types.js';
import { reducer, initialState, buildInitialState } from './socket/reducer.js';

export * from './socket/types.js';

function safeGetItem(key: string): string | null {
  try {
    if (typeof localStorage !== 'undefined' && typeof localStorage.getItem === 'function') {
      return localStorage.getItem(key);
    }
  } catch {
    // Ignore storage access errors
  }
  return null;
}

interface ParsedAuthCallback {
  accessToken?: string;
  refreshToken?: string;
  tokenHash?: string;
  type?: AuthOtpType;
  errorDescription?: string;
}

function consumeUrlAuthCallback(): ParsedAuthCallback | null {
  if (typeof window === 'undefined') return null;
  const hashRaw = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const searchRaw = window.location.search.startsWith('?') ? window.location.search.slice(1) : '';
  if (!hashRaw && !searchRaw) return null;

  const hashParams = new URLSearchParams(hashRaw);
  const searchParams = new URLSearchParams(searchRaw);

  const accessToken = hashParams.get('access_token') || searchParams.get('access_token') || undefined;
  const refreshToken = hashParams.get('refresh_token') || searchParams.get('refresh_token') || undefined;
  const tokenHash = searchParams.get('token_hash') || hashParams.get('token_hash') || undefined;
  const rawType = hashParams.get('type') || searchParams.get('type') || undefined;
  const errorDescription =
    hashParams.get('error_description') || searchParams.get('error_description') || undefined;

  if (!accessToken && !tokenHash && !errorDescription) return null;

  const type: AuthOtpType | undefined =
    rawType === 'recovery' || rawType === 'email' || rawType === 'email_change' || rawType === 'signup'
      ? rawType
      : 'signup';

  try {
    window.history.replaceState({}, '', window.location.pathname);
  } catch {
    // Ignore history API restrictions in embedded contexts
  }

  return { accessToken, refreshToken, tokenHash, type, errorDescription };
}

export function useGameSocket(): { state: ClientState; actions: GameActions } {
  const [state, dispatch] = useReducer(reducer, initialState, buildInitialState);
  const socketRef = useRef<Socket | null>(null);
  const voiceSignalListenersRef = useRef<Set<(payload: VoiceSignalRelayPayload) => void>>(new Set());
  const reactionSeqRef = useRef(0);
  const playerIdRef = useRef<string | null>(null);
  const roomIdRef = useRef<string | null>(null);
  const stateRef = useRef<ClientState>(state);
  const pendingDaaluuPlayRef = useRef<{ roundId: string; motionSerial: number; activePlayerId: string | null } | null>(null);
  const triedFallbackRejoinRef = useRef(false);
  const leaderboardTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  playerIdRef.current = state.playerId;
  roomIdRef.current = state.roomId;
  stateRef.current = state;
  // Release only after React has rendered the authoritative move, so a click
  // between the socket event and that render cannot resend the stale play.
  useEffect(() => {
    const pending = pendingDaaluuPlayRef.current;
    if (pending && (state.game?.daaluu?.shuffle.roundId !== pending.roundId ||
      state.game.daaluu.motion.serial !== pending.motionSerial || state.game.activePlayerId !== pending.activePlayerId))
      pendingDaaluuPlayRef.current = null;
  }, [state.game]);

  useEffect(() => {
    const urlCallback = consumeUrlAuthCallback();
    if (urlCallback?.errorDescription) {
      dispatch({ type: 'ERROR', message: urlCallback.errorDescription });
    } else if (urlCallback?.accessToken && urlCallback.type === 'recovery') {
      dispatch({
        type: 'AUTH_NOTICE',
        payload: {
          kind: 'password_reset_ready',
          recoveryToken: urlCallback.accessToken,
          message: 'Please enter your new password below.',
        },
      });
    } else if (urlCallback?.accessToken) {
      localStorage.setItem('big2.authToken', urlCallback.accessToken);
      if (urlCallback.refreshToken) localStorage.setItem('big2.refreshToken', urlCallback.refreshToken);
      else localStorage.removeItem('big2.refreshToken');
      dispatch({
        type: 'AUTH_NOTICE',
        payload: {
          kind: 'email_verified',
          message: urlCallback.type === 'signup'
            ? 'Email address verified successfully!'
            : 'Signed in successfully!',
        },
      });
    }

    const initialSession = safeGetItem('big2.session');
    const initialAuthToken = safeGetItem('big2.authToken');
    const initialRefreshToken = safeGetItem('big2.refreshToken');
    const authPayload: Record<string, string> = {};
    if (initialSession) authPayload.sessionId = initialSession;
    if (initialAuthToken) authPayload.authToken = initialAuthToken;
    if (initialRefreshToken) authPayload.refreshToken = initialRefreshToken;

    const customSocketUrl = (globalThis as { __BIG2_SOCKET_URL__?: string }).__BIG2_SOCKET_URL__;
    const socketOptions = {
      auth: authPayload,
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 800,
      reconnectionDelayMax: 4000,
      ...(customSocketUrl ? { forceNew: true } : {}),
    };
    const socket = customSocketUrl ? io(customSocketUrl, socketOptions) : io(socketOptions);
    socketRef.current = socket;

    const emitRejoin = () => {
      const isRoomRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/room/');
      if (!roomIdRef.current && !isRoomRoute) return;
      const savedSession = localStorage.getItem('big2.session');
      const savedRoom = localStorage.getItem('big2.lastRoom');
      const savedName = localStorage.getItem('big2.name') || '';
      if (savedSession || (savedRoom && savedName)) {
        socket.emit(C2S.REJOIN_ROOM, {
          sessionId: savedSession ?? undefined,
          roomId: savedRoom ?? undefined,
          playerName: savedName || undefined,
        });
      }
    };

    const pingServer = () => {
      if (!socket.connected) return;
      const start = Date.now();
      socket.emit(C2S.PING, {}, (ack?: { t?: number }) => {
        const end = Date.now();
        const rtt = Math.max(0, end - start);
        const serverTime = ack?.t;
        const serverOffsetMs =
          typeof serverTime === 'number' ? Math.round(serverTime + rtt / 2 - end) : 0;
        dispatch({ type: 'LATENCY', ms: rtt, serverOffsetMs });
      });
    };

    socket.io.on('reconnect_attempt', () => {
      const latestAuth: Record<string, string> = {};
      const sid = localStorage.getItem('big2.session');
      const tok = localStorage.getItem('big2.authToken');
      const refresh = localStorage.getItem('big2.refreshToken');
      const isRoomRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/room/');
      if (sid && (roomIdRef.current || isRoomRoute)) latestAuth.sessionId = sid;
      if (tok) latestAuth.authToken = tok;
      if (refresh) latestAuth.refreshToken = refresh;
      socket.auth = latestAuth;
    });

    let hasConnectedOnce = false;
    socket.on('connect', () => {
      dispatch({ type: 'CONNECTED' });
      trackEvent('socket_connected', { is_reconnect: hasConnectedOnce });
      if (!hasConnectedOnce && urlCallback?.tokenHash) {
        socket.emit(C2S.AUTH_VERIFY_EMAIL, {
          tokenHash: urlCallback.tokenHash,
          type: urlCallback.type,
        });
      }
      // Actively request room/seat resync on both fresh connect and reconnect
      emitRejoin();
      hasConnectedOnce = true;
      pingServer();
    });
    socket.on('disconnect', (reason) => {
      pendingDaaluuPlayRef.current = null;
      dispatch({ type: 'DISCONNECTED' });
      trackEvent('socket_disconnected', { reason });
    });

    socket.on(S2C.CAPABILITIES, (payload: { supportedGames: GameType[] }) => {
      dispatch({ type: 'CAPABILITIES', supportedGames: payload.supportedGames });
    });
    socket.on(S2C.AUTH_STATE, (payload: AuthStatePayload) => {
      const prevProfile = stateRef.current.profile;
      const currentAuth = (socket.auth && typeof socket.auth === 'object' ? socket.auth : {}) as Record<string, string>;
      const updatedAuth = { ...currentAuth };
      if (payload.authToken) updatedAuth.authToken = payload.authToken;
      else if (payload.authToken === null) delete updatedAuth.authToken;
      if (payload.refreshToken) updatedAuth.refreshToken = payload.refreshToken;
      else if (payload.refreshToken === null || payload.authToken === null) delete updatedAuth.refreshToken;
      socket.auth = updatedAuth;
      setAnalyticsUser(payload.profile ?? null);
      if (payload.profile && (!prevProfile || prevProfile.id !== payload.profile.id)) {
        trackEvent('auth_authenticated', {
          tier: payload.profile.tier,
          is_admin: !!payload.profile.isAdmin,
        });
      }
      if (
        payload.profile?.subscriptionExpiresAt &&
        prevProfile &&
        payload.profile.subscriptionExpiresAt !== prevProfile.subscriptionExpiresAt
      ) {
        trackEvent('purchase', {
          transaction_id: stateRef.current.paymentInfo?.invoiceId ?? `vip_${Date.now()}`,
          currency: 'MNT',
          value: MEMBERSHIP_PRICE_MNT,
          item_name: 'VIP Monthly Subscription',
        });
      }
      if (payload.weeklyBonusGranted) {
        trackEvent('weekly_bonus_granted', {
          amount: payload.weeklyBonusGranted,
          tier: payload.profile?.tier ?? 'free',
        });
      }
      dispatch({ type: 'AUTH_STATE', payload });
    });

    socket.on(S2C.AUTH_NOTICE, (payload: AuthNoticePayload) => {
      trackEvent('auth_notice', { kind: payload.kind });
      dispatch({ type: 'AUTH_NOTICE', payload });
    });
    socket.on(S2C.AUTH_OAUTH_URL, (payload: { provider: string; url: string }) => {
      if (payload?.url) {
        window.location.href = payload.url;
      }
    });

    socket.on(S2C.ROOM_JOINED, (payload: RoomJoinedPayload) => {
      triedFallbackRejoinRef.current = false;
      const currentAuth = (socket.auth && typeof socket.auth === 'object' ? socket.auth : {}) as Record<string, string>;
      socket.auth = { ...currentAuth, sessionId: payload.sessionId };
      const gameType = payload.gameType ?? stateRef.current.lobby?.config.gameType ?? stateRef.current.game?.gameType ?? 'big2';
      const mode = payload.mode ?? stateRef.current.lobby?.config.mode ?? 'free';
      const eventData = {
        room_id: payload.roomId,
        game_type: gameType,
        mode,
        has_history: !!payload.history,
      };
      trackEvent('room_joined', eventData);
      trackEvent(`room_joined_${gameType}`, eventData);
      dispatch({ type: 'ROOM_JOINED', payload });
    });
    socket.on(S2C.LOBBY_UPDATE, (payload: LobbyUpdatePayload) => dispatch({ type: 'LOBBY_UPDATE', payload }));
    socket.on(S2C.PUBLIC_ROOMS, (payload: PublicRoomsPayload) => {
      if (payload && Array.isArray(payload.rooms)) {
        dispatch({ type: 'PUBLIC_ROOMS', payload: payload.rooms });
      }
    });
    socket.on(S2C.MATCHMAKING_STATUS, (payload: MatchmakingStatusPayload) => {
      if (payload?.status === 'found') {
        playMatchFoundSound();
      }
      dispatch({ type: 'MATCHMAKING_STATUS', payload });
    });
    socket.on(S2C.SYNC_GAME_STATE, (payload: GameView) => dispatch({ type: 'SYNC_GAME_STATE', payload }));
    socket.on(S2C.GAME_START, (payload?: GameStartPayload) => {
      dispatch({ type: 'CLEAR_GAME_OVER' });
      const gameType =
        payload?.gameType ??
        stateRef.current.lobby?.config.gameType ??
        stateRef.current.game?.gameType ??
        'big2';
      const mode = payload?.mode ?? stateRef.current.lobby?.config.mode ?? 'free';
      const players = payload?.players ?? stateRef.current.lobby?.players ?? [];
      const playerCount = players.length;
      const botCount = players.filter((p) => p.isBot).length;
      const humanCount = playerCount - botCount;
      const isSoloVsBots = playerCount > 1 && humanCount === 1;

      if (gameType !== 'daaluu') {
        playShuffleSound();
      }

      const eventData = {
        room_id: roomIdRef.current,
        game_type: gameType,
        mode,
        player_count: playerCount,
        bot_count: botCount,
        human_count: humanCount,
        is_bot_match: botCount > 0,
        is_solo_vs_bots: isSoloVsBots,
        round: payload?.round,
      };

      trackEvent('game_start', eventData);
      trackEvent(`game_start_${gameType}`, eventData);
    });
    socket.on(S2C.MOVE_ACCEPTED, (payload: MoveAcceptedPayload) => {
      playCardPlaceSound(payload.cards?.length ?? 1);
    });
    socket.on(S2C.PLAYER_PASSED, () => {
      playPassSound();
    });
    socket.on(S2C.GAME_OVER, (payload: GameOverPayload) => {
      const didWin = payload.winnerId === playerIdRef.current;
      playGameOverSound(didWin);
      const gameType = payload.gameType ?? stateRef.current.game?.gameType ?? 'big2';
      const players = stateRef.current.game?.players ?? [];
      const playerCount = players.length;
      const botCount = players.filter((p) => p.isBot).length;
      const humanCount = playerCount - botCount;

      const eventData = {
        room_id: roomIdRef.current,
        game_type: gameType,
        mode: stateRef.current.lobby?.config.mode ?? 'free',
        did_win: didWin,
        player_count: playerCount,
        bot_count: botCount,
        human_count: humanCount,
        is_bot_match: botCount > 0,
        match_over: !!payload.matchOver,
      };

      trackEvent('game_over', eventData);
      trackEvent(`game_over_${gameType}`, eventData);
      dispatch({ type: 'GAME_OVER', payload });
    });
    socket.on(S2C.PLAYER_REACTION, (payload: PlayerReactionPayload) => {
      const seq = ++reactionSeqRef.current;
      dispatch({
        type: 'PLAYER_REACTION',
        payload: { ...payload, seq },
      });
      setTimeout(() => {
        dispatch({ type: 'CLEAR_REACTION', playerId: payload.playerId, seq });
      }, 3200);
    });
    socket.on(S2C.TABLE_THROW, (payload: TableThrowBroadcast) => {
      dispatch({ type: 'TABLE_THROW', payload });
      setTimeout(() => {
        dispatch({ type: 'CLEAR_TABLE_THROW', seq: payload.seq });
      }, 4500);
    });
    socket.on(S2C.VOICE_TAUNT, (payload: VoiceTauntBroadcast) => {
      dispatch({ type: 'VOICE_TAUNT', payload });
      setTimeout(() => {
        dispatch({ type: 'CLEAR_VOICE_TAUNT', seq: payload.seq });
      }, 4200);
    });
    socket.on(S2C.VOICE_PARTICIPANTS, (payload: VoiceParticipantsPayload) => {
      dispatch({ type: 'VOICE_PARTICIPANTS', payload });
    });
    socket.on(S2C.VOICE_SIGNAL, (payload: VoiceSignalRelayPayload) => {
      for (const listener of voiceSignalListenersRef.current) {
        try {
          listener(payload);
        } catch {
          // Isolate WebRTC signaling callbacks from socket state
        }
      }
    });
    socket.on(S2C.PAYMENT_INFO, (payload: PaymentInfoPayload) => {
      trackEvent('qpay_invoice_created', {
        invoice_id: payload.invoiceId,
        bank_link_count: payload.urls?.length ?? 0,
        amount: payload.amount,
      });
      dispatch({ type: 'PAYMENT_INFO', payload });
    });
    socket.on(S2C.LEADERBOARD_DATA, (payload: LeaderboardPayload) => {
      if (leaderboardTimeoutRef.current) {
        clearTimeout(leaderboardTimeoutRef.current);
        leaderboardTimeoutRef.current = null;
      }
      dispatch({ type: 'LEADERBOARD_DATA', payload });
    });
    socket.on(S2C.FRIENDS_DATA, (payload: FriendsDataPayload) => {
      dispatch({ type: 'FRIENDS_DATA', payload });
    });
    socket.on(S2C.PLAYER_PROFILE, (payload: PublicPlayerProfile) => {
      dispatch({ type: 'PLAYER_PROFILE', payload });
    });
    socket.on(S2C.FRIEND_REQUEST_RECEIVED, () => {
      socket.emit(C2S.FRIENDS_GET);
    });
    socket.on(S2C.BILLING_HISTORY, (payload: PaymentRecord[]) => dispatch({ type: 'BILLING_HISTORY', payload }));
    socket.on(S2C.COUPON_QUOTE, (payload: CouponQuote) => dispatch({ type: 'COUPON_QUOTE', payload }));
    socket.on(S2C.ROOM_HISTORY, (payload: RoomSessionHistory) => dispatch({ type: 'ROOM_HISTORY', payload }));
    socket.on(S2C.ADMIN_DATA, (payload: BillingPage) => dispatch({ type: 'ADMIN_DATA', payload }));
    socket.on(S2C.ADMIN_SAVED, (payload: { requestId: string }) => dispatch({ type: 'ADMIN_SAVED', requestId: payload.requestId }));
    socket.on(S2C.FEEDBACK_SUBMITTED, (payload: { id: string }) => {
      trackEvent('feedback_submitted', { id: payload.id });
      dispatch({ type: 'FEEDBACK_SUBMITTED', id: payload.id });
    });
    socket.on(S2C.SESSION_EXPIRED, () => {
      const currentAuth = (socket.auth && typeof socket.auth === 'object' ? socket.auth : {}) as Record<string, string>;
      const { sessionId: _removed, ...rest } = currentAuth;
      socket.auth = rest;
      localStorage.removeItem('big2.session');

      // If we still have a recent room & player name and are directly on a room route, try reclaiming our seat once
      const isRoomRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/room/');
      const savedRoom = localStorage.getItem('big2.lastRoom');
      const savedName = localStorage.getItem('big2.name') || '';
      trackEvent('session_expired', { fallback_rejoin_attempted: !triedFallbackRejoinRef.current && isRoomRoute && !!(savedRoom && savedName) });
      if (!triedFallbackRejoinRef.current && isRoomRoute && savedRoom && savedName) {
        triedFallbackRejoinRef.current = true;
        socket.emit(C2S.REJOIN_ROOM, { roomId: savedRoom, playerName: savedName });
        return;
      }
      dispatch({ type: 'SESSION_EXPIRED' });
    });
    socket.on(S2C.PLAYER_KICKED, (payload: PlayerKickedPayload) => {
      const currentAuth = (socket.auth && typeof socket.auth === 'object' ? socket.auth : {}) as Record<string, string>;
      const { sessionId: _removed, ...rest } = currentAuth;
      socket.auth = rest;
      try {
        localStorage.removeItem('big2.session');
        localStorage.removeItem('big2.lastRoom');
      } catch {}
      trackEvent('player_kicked', { room_id: roomIdRef.current });
      dispatch({
        type: 'PLAYER_KICKED',
        message: payload?.reason === 'kicked_by_host'
          ? 'You were removed from the room by the host.'
          : 'You were removed from the room.',
      });
    });
    socket.on(S2C.ERROR, (payload: { message: string }) => {
      pendingDaaluuPlayRef.current = null;
      trackEvent('server_error', {
        message: payload.message,
        room_id: roomIdRef.current,
      });
      dispatch({ type: 'ERROR', message: payload.message });
    });

    // Reconnect immediately when mobile browser tab wakes up or network comes back online
    const handleWakeUp = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const isRoomRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/room/');
      if (socket.disconnected) {
        socket.connect();
      } else if (!roomIdRef.current && isRoomRoute && localStorage.getItem('big2.session')) {
        emitRejoin();
      }
      if (socket.connected) {
        pingServer();
      }
    };
    window.addEventListener('online', handleWakeUp);
    document.addEventListener('visibilitychange', handleWakeUp);

    const pingInterval = setInterval(pingServer, 5000);

    return () => {
      clearInterval(pingInterval);
      window.removeEventListener('online', handleWakeUp);
      document.removeEventListener('visibilitychange', handleWakeUp);
      if (leaderboardTimeoutRef.current) {
        clearTimeout(leaderboardTimeoutRef.current);
        leaderboardTimeoutRef.current = null;
      }
      socket.disconnect();
    };
  }, []);

  const actions: GameActions = useMemo(() => ({
    daaluuAction: action => {
      const roundId = stateRef.current.game?.daaluu?.shuffle.roundId;
      const stage = stateRef.current.game?.daaluu?.stage ?? 'unknown';
      if (roundId && action.type === 'play') {
        if (pendingDaaluuPlayRef.current) return;
        pendingDaaluuPlayRef.current = {
          roundId,
          motionSerial: stateRef.current.game!.daaluu!.motion.serial,
          activePlayerId: stateRef.current.game!.activePlayerId,
        };
      }
      trackEvent('daaluu_action', {
        action_type: action.type,
        stage,
        room_id: roomIdRef.current,
      });
      if (action.type === 'play') {
        trackEvent('daaluu_tile_played', {
          tile_count: action.tileIds.length,
          room_id: roomIdRef.current,
        });
      } else if (action.type === 'tribute') {
        trackEvent('daaluu_tribute', {
          tile_count: action.tileIds.length,
          room_id: roomIdRef.current,
        });
      } else if (action.type === 'call') {
        trackEvent('daaluu_janlii_call', {
          kind_id: action.kindId,
          room_id: roomIdRef.current,
        });
      }
      if (roundId) socketRef.current?.emit(C2S.DAALUU_ACTION, { roundId, action });
    },
    daaluuSweep: command => {
      trackEvent('daaluu_sweep', { room_id: roomIdRef.current });
      socketRef.current?.emit(C2S.DAALUU_SWEEP, command);
    },
    daaluuRetry: () => {
      trackEvent('daaluu_retry', { room_id: roomIdRef.current });
      const shuffle = stateRef.current.game?.daaluu?.shuffle;
      if (shuffle) socketRef.current?.emit(C2S.DAALUU_RETRY, { roundId: shuffle.roundId, attempt: shuffle.attempt });
    },
    loadRoomHistory: (link) => {
      trackEvent('room_history_view', { room_id: link.roomId });
      dispatch({ type: 'ROOM_HISTORY_LOADING' });
      socketRef.current?.emit(C2S.ROOM_HISTORY, { id: link.id, key: link.key });
    },
    loadBillingHistory: () => {
      trackEvent('billing_history_view');
      socketRef.current?.emit(C2S.BILLING_HISTORY);
    },
    loadAdmin: (resource, page, search) => {
      trackEvent('admin_list_view', { resource, page, has_search: !!search.trim() });
      dispatch({ type: 'ADMIN_LOADING' });
      socketRef.current?.emit(C2S.ADMIN_LIST, { resource, page, search });
    },
    adminMutate: (mutation) => {
      trackEvent('admin_mutate', { kind: mutation.kind });
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.ADMIN_MUTATE, mutation);
    },
    register: (email: string, password: string, displayName: string) => {
      trackEvent('sign_up_attempt', { method: 'email' });
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'CLEAR_AUTH_NOTICE' });
      socketRef.current?.emit(C2S.AUTH_REGISTER, { email, password, displayName });
    },
    login: (email: string, password: string) => {
      trackEvent('login_attempt', { method: 'email' });
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'CLEAR_AUTH_NOTICE' });
      socketRef.current?.emit(C2S.AUTH_LOGIN, { email, password });
    },
    loginWithOAuth: (provider: OAuthProvider = 'google') => {
      trackEvent('auth_oauth_attempt', { provider });
      dispatch({ type: 'CLEAR_ERROR' });
      dispatch({ type: 'CLEAR_AUTH_NOTICE' });
      const sock = socketRef.current;
      if (sock) {
        sock.emit(C2S.AUTH_OAUTH_URL, { provider }, (res?: { url?: string }) => {
          if (res?.url) {
            window.location.href = res.url;
          }
        });
      } else {
        window.location.href = `/auth/${encodeURIComponent(provider)}`;
      }
    },
    logout: () => {
      trackEvent('logout');
      setAnalyticsUser(null);
      dispatch({ type: 'CLEAR_AUTH_NOTICE' });
      socketRef.current?.emit(C2S.AUTH_LOGOUT, {});
    },
    verifyEmail: (params) => {
      trackEvent('auth_verify_email', { type: params.type ?? 'signup' });
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_VERIFY_EMAIL, params);
    },
    resendVerification: (email) => {
      trackEvent('auth_resend_verification');
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_RESEND_VERIFICATION, email ? { email } : {});
    },
    forgotPassword: (email) => {
      trackEvent('auth_forgot_password');
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_FORGOT_PASSWORD, { email });
    },
    resetPassword: (params) => {
      trackEvent('auth_reset_password', { has_recovery_token: !!params.recoveryToken });
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_RESET_PASSWORD, params);
    },
    changePassword: (currentPassword, newPassword) => {
      trackEvent('auth_change_password');
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_CHANGE_PASSWORD, { currentPassword, newPassword });
    },
    updateProfile: (updates) => {
      trackEvent('auth_update_profile', {
        has_name: updates.displayName !== undefined,
        has_email: updates.email !== undefined,
      });
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_UPDATE_PROFILE, updates);
    },
    deleteAccount: (currentPassword) => {
      trackEvent('auth_delete_account');
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.AUTH_DELETE_ACCOUNT, { currentPassword });
    },
    clearAuthNotice: () => dispatch({ type: 'CLEAR_AUTH_NOTICE' }),
    claimWeeklyTsai: () => {
      trackEvent('weekly_bonus_claim_click', { tier: stateRef.current.profile?.tier ?? 'free' });
      socketRef.current?.emit(C2S.CLAIM_WEEKLY_TSAI, {});
    },
    getLeaderboard: () => {
      dispatch({ type: 'LEADERBOARD_LOADING' });
      if (leaderboardTimeoutRef.current) clearTimeout(leaderboardTimeoutRef.current);
      leaderboardTimeoutRef.current = setTimeout(() => {
        dispatch({ type: 'LEADERBOARD_DATA', payload: stateRef.current.leaderboard ?? { entries: [] } });
      }, 7000);
      socketRef.current?.emit(C2S.GET_LEADERBOARD, {});
    },
    getFriends: () => {
      dispatch({ type: 'FRIENDS_LOADING' });
      socketRef.current?.emit(C2S.FRIENDS_GET, {});
    },
    inspectPlayer: (userId: string) => {
      dispatch({ type: 'PLAYER_PROFILE_LOADING' });
      socketRef.current?.emit(C2S.GET_PLAYER_PROFILE, { targetUserId: userId });
    },
    clearInspectedPlayer: () => {
      dispatch({ type: 'CLEAR_INSPECTED_PLAYER' });
    },
    sendFriendRequest: (target) => {
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.FRIEND_REQUEST_SEND, target);
    },
    respondFriendRequest: (requestId, accept) => {
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.FRIEND_REQUEST_RESPOND, { requestId, accept });
    },
    removeFriend: (friendUserId) => {
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.FRIEND_REMOVE, { friendUserId });
    },
    initiatePayment: (tier?: UserTier, couponCode?: string) => {
      const targetTier = tier ?? 'paid';
      trackEvent('begin_checkout', { tier: targetTier, currency: 'MNT', value: MEMBERSHIP_PRICE_MNT });
      socketRef.current?.emit(C2S.INITIATE_PAYMENT, { tier: targetTier, ...(couponCode ? { couponCode: couponCode.trim().toUpperCase() } : {}) });
    },
    checkPayment: (invoiceId: string, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        trackEvent('qpay_check_payment', { invoice_id: invoiceId });
      }
      socketRef.current?.emit(C2S.CHECK_PAYMENT, { invoiceId, silent: options?.silent });
    },
    redeemCoupon: (code: string) => {
      trackEvent('coupon_redeem_attempt', { code_length: code.trim().length });
      dispatch({ type: 'CLEAR_COUPON_QUOTE' });
      dispatch({ type: 'CLEAR_ERROR' });
      socketRef.current?.emit(C2S.REDEEM_COUPON, { code });
    },
    clearCouponQuote: () => dispatch({ type: 'CLEAR_COUPON_QUOTE' }),
    clearPaymentInfo: () => dispatch({ type: 'CLEAR_PAYMENT_INFO' }),
    clearWeeklyBonusNotice: () => dispatch({ type: 'CLEAR_WEEKLY_NOTICE' }),
    createRoom: (
      playerName: string,
      mode: RoomMode = 'free',
      customRoomName?: string,
      gameType: GameType = 'big2',
      voiceChatEnabled?: boolean,
      isPublic?: boolean,
    ) => {
      const eventData = {
        game_type: gameType,
        mode,
        has_custom_name: !!customRoomName?.trim(),
        voice_chat_enabled: !!voiceChatEnabled,
        is_public: !!isPublic,
      };
      trackEvent('room_create', eventData);
      trackEvent(`room_create_${gameType}`, eventData);
      socketRef.current?.emit(C2S.CREATE_ROOM, {
        playerName,
        mode,
        gameType,
        ...(customRoomName?.trim() ? { customRoomName: customRoomName.trim() } : {}),
        ...(voiceChatEnabled !== undefined ? { voiceChatEnabled } : {}),
        ...(isPublic !== undefined ? { isPublic } : {}),
      });
    },
    quickPlay: (
      playerName: string,
      gameType: GameType = 'big2',
      mode: RoomMode = 'free',
      queue = true,
    ) => {
      trackEvent('quick_play_click', { game_type: gameType, mode, queue });
      socketRef.current?.emit(C2S.QUICK_PLAY, {
        playerName,
        gameType,
        mode,
        queue,
      });
    },
    cancelMatchmaking: () => {
      trackEvent('matchmaking_cancelled');
      socketRef.current?.emit(C2S.CANCEL_MATCHMAKING, {});
      dispatch({ type: 'CLEAR_MATCHMAKING' });
    },
    refreshPublicRooms: () => {
      socketRef.current?.emit(C2S.LIST_PUBLIC_ROOMS, {});
    },
    joinRoom: (roomId: string, playerName: string, sessionId?: string) => {
      trackEvent('room_join_attempt', { room_id: roomId });
      const savedRoom = safeGetItem('big2.lastRoom');
      const savedSession = safeGetItem('big2.session');
      const sid =
        sessionId ??
        (savedRoom && savedRoom.toUpperCase() === roomId.trim().toUpperCase() ? savedSession : undefined) ??
        undefined;
      socketRef.current?.emit(C2S.JOIN_ROOM, {
        roomId,
        playerName,
        ...(sid ? { sessionId: sid } : {}),
      });
    },
    rejoinRoom: () => {
      const sock = socketRef.current;
      if (!sock) return;
      if (sock.disconnected) {
        sock.connect();
      }
      const savedSession = localStorage.getItem('big2.session');
      const savedRoom = localStorage.getItem('big2.lastRoom');
      const savedName = localStorage.getItem('big2.name') || '';
      trackEvent('room_rejoin_attempt', {
        room_id: savedRoom,
        has_session: !!savedSession,
      });
      sock.emit(C2S.REJOIN_ROOM, {
        sessionId: savedSession ?? undefined,
        roomId: savedRoom ?? undefined,
        playerName: savedName || undefined,
      });
    },
    leaveRoom: () => {
      const currentPhase = stateRef.current.game?.phase ?? stateRef.current.lobby?.phase ?? 'unknown';
      const gt = stateRef.current.game?.gameType ?? stateRef.current.lobby?.config.gameType ?? 'big2';
      trackEvent('room_leave', {
        room_id: stateRef.current.roomId,
        game_type: gt,
        phase: currentPhase,
      });
      if (currentPhase === 'playing') {
        trackEvent('game_abandoned', {
          room_id: stateRef.current.roomId,
          game_type: gt,
        });
        trackEvent(`game_abandoned_${gt}`, {
          room_id: stateRef.current.roomId,
        });
      }
      socketRef.current?.emit(C2S.LEAVE_ROOM, {});
      if (socketRef.current && typeof socketRef.current.auth === 'object') {
        const { sessionId: _removed, ...rest } = socketRef.current.auth as Record<string, string>;
        socketRef.current.auth = rest;
      }
      dispatch({ type: 'LEAVE_ROOM' });
    },
    kickPlayer: (playerId: string) => {
      trackEvent('kick_player_attempt', { target_player_id: playerId, room_id: stateRef.current.roomId });
      socketRef.current?.emit(C2S.KICK_PLAYER, { playerId });
    },
    chooseSeat: (seat: number, playerId?: string) => {
      socketRef.current?.emit(C2S.CHOOSE_SEAT, playerId ? { seat, playerId } : { seat });
    },
    configureRoom: (config: RoomConfig) => {
      trackEvent('room_configure', {
        game_type: config.gameType ?? 'big2',
        mode: config.mode ?? 'free',
        seats: config.seats,
        fill_with_bots: config.fillWithBots,
        voice_chat_enabled: !!config.voiceChatEnabled,
      });
      socketRef.current?.emit(C2S.ROOM_CONFIG, config);
    },
    startGame: (options?: { restartMatch?: boolean }) => {
      const gameType = stateRef.current.lobby?.config.gameType ?? 'big2';
      const isPlayAgain = !!stateRef.current.lastGameOver || stateRef.current.lobby?.phase === 'finished';
      const eventData = {
        room_id: stateRef.current.roomId,
        game_type: gameType,
        mode: stateRef.current.lobby?.config.mode ?? 'free',
        is_play_again: isPlayAgain,
        restart_match: Boolean(options?.restartMatch),
      };
      trackEvent('game_start_click', eventData);
      trackEvent(`game_start_click_${gameType}`, eventData);
      if (isPlayAgain) {
        trackEvent('rematch_click', { game_type: gameType, room_id: stateRef.current.roomId });
        trackEvent(`rematch_click_${gameType}`, { room_id: stateRef.current.roomId });
      }
      socketRef.current?.emit(C2S.START_GAME, options ?? {});
    },
    returnToLobby: () => {
      trackEvent('return_to_lobby_click', {
        room_id: stateRef.current.roomId,
      });
      socketRef.current?.emit(C2S.RETURN_TO_LOBBY, {});
    },
    playCards: (cards: string[] | number | any) => {
      const gt = stateRef.current.game?.gameType ?? 'big2';
      const stage = gt === 'twentyone'
        ? stateRef.current.game?.twentyone?.stage ?? 'playing'
        : stateRef.current.game?.muushig?.stage ?? 'playing';

      if (gt === 'twentyone') {
        if (stage === 'betting' || typeof cards === 'number') {
          trackEvent('twentyone_bet', {
            amount: typeof cards === 'number' ? cards : 0,
            room_id: roomIdRef.current,
          });
        } else if (Array.isArray(cards) && cards.length === 0) {
          trackEvent('twentyone_hit', {
            room_id: roomIdRef.current,
          });
        }
      } else if (gt === 'muushig') {
        if (stage === 'exchange' || stage === 'trump-exchange') {
          trackEvent('muushig_discard', {
            count: Array.isArray(cards) ? cards.length : 1,
            room_id: roomIdRef.current,
          });
        } else {
          trackEvent('muushig_card_played', {
            card_count: Array.isArray(cards) ? cards.length : 1,
            room_id: roomIdRef.current,
          });
        }
      } else if (gt === 'big2') {
        trackEvent('big2_cards_played', {
          card_count: Array.isArray(cards) ? cards.length : 1,
          room_id: roomIdRef.current,
        });
      }

      trackEvent('cards_played', {
        game_type: gt,
        stage,
        card_count: Array.isArray(cards) ? cards.length : typeof cards === 'number' ? 0 : 1,
      });
      socketRef.current?.emit(C2S.PLAY_CARDS, { cards });
    },
    passTurn: () => {
      const gt = stateRef.current.game?.gameType ?? 'big2';
      const stage = gt === 'twentyone'
        ? stateRef.current.game?.twentyone?.stage ?? 'playing'
        : stateRef.current.game?.muushig?.stage ?? 'playing';

      if (gt === 'twentyone') {
        trackEvent('twentyone_stand', { room_id: roomIdRef.current });
      } else if (gt === 'muushig') {
        trackEvent('muushig_fold', { room_id: roomIdRef.current });
      } else if (gt === 'big2') {
        trackEvent('big2_turn_passed', { room_id: roomIdRef.current });
      }

      trackEvent('turn_passed', {
        game_type: gt,
        stage,
      });
      socketRef.current?.emit(C2S.PASS_TURN, {});
    },
    setAutoPlay: (enabled: boolean) => {
      socketRef.current?.emit(C2S.SET_AUTOPLAY, { enabled });
    },
    pauseAction: (action: PauseActionType) => {
      trackEvent('pause_action', {
        action,
        game_type: stateRef.current.game?.gameType ?? 'big2',
        room_id: stateRef.current.roomId,
      });
      socketRef.current?.emit(C2S.PAUSE_ACTION, { action });
    },
    sendReaction: (reactionId: ReactionId) => {
      trackEvent('reaction_sent', {
        reaction_id: reactionId,
        phase: stateRef.current.game?.phase === 'playing' ? 'game' : 'lobby',
      });
      socketRef.current?.emit(C2S.SEND_REACTION, { reactionId });
    },
    sendTableThrow: (targetSeat: number, throwableId: ThrowableId) => {
      trackEvent('table_throw_sent', {
        throwable_id: throwableId,
        target_seat: targetSeat,
        room_id: stateRef.current.roomId,
      });
      socketRef.current?.emit(C2S.TABLE_THROW, { targetSeat, throwableId });
    },
    sendVoiceTaunt: (tauntId: VoiceTauntId, targetSeat?: number) => {
      trackEvent('voice_taunt_sent', {
        taunt_id: tauntId,
        target_seat: targetSeat,
        room_id: stateRef.current.roomId,
      });
      socketRef.current?.emit(C2S.VOICE_TAUNT, { tauntId, targetSeat });
    },
    setVoiceState: (joined: boolean, muted: boolean) => {
      trackEvent('voice_state_changed', {
        joined,
        muted,
        room_id: stateRef.current.roomId,
      });
      socketRef.current?.emit(C2S.VOICE_STATE, { joined, muted });
    },
    sendVoiceSignal: (payload: VoiceSignalPayload) => {
      socketRef.current?.emit(C2S.VOICE_SIGNAL, payload);
    },
    subscribeVoiceSignal: (listener: (payload: VoiceSignalRelayPayload) => void) => {
      voiceSignalListenersRef.current.add(listener);
      return () => {
        voiceSignalListenersRef.current.delete(listener);
      };
    },
    clearGameOver: () => dispatch({ type: 'CLEAR_GAME_OVER' }),
    clearMatchLoading: () => dispatch({ type: 'CLEAR_MATCH_LOADING' }),
    clearError: () => dispatch({ type: 'CLEAR_ERROR' }),
    submitFeedback: (payload: SubmitFeedbackPayload) => {
      socketRef.current?.emit(C2S.SUBMIT_FEEDBACK, payload);
    },
    clearFeedbackNotice: () => dispatch({ type: 'CLEAR_FEEDBACK_NOTICE' }),
  }), []);

  return { state, actions };
}
